import { call, put, select } from "redux-saga/effects";
import { EHelpOpenFor, EHelpOpenIn, EHelpType, IGetHelp, setHelpConfig, setHelpDetailData, setHelpView } from "@/Redux/Ducks/helpSlice";
import { toggleLoader } from "@/Redux/Ducks/loaderSlice";
import { PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "@/Redux/configureStore";
import { LoadHelpObject, LoadHelpObjectTags, requestGetDocumentHelp, requestGetLoadHelpData } from "../Requests/help";
import { setGlobalMessage } from "@/Redux/Ducks/messageSlice";
import { IDocumentHelpView, ISubGrid, KeyValueObject } from "@/UI/Interfaces/IBaseUI";
import { ICriteriaFieldValue } from "@/UI/Interfaces/IFieldConfig";

export function* handleGetHelpConfig({ payload }: PayloadAction<IGetHelp>): any {
  try {
    yield put(toggleLoader(true));

    const tabDetail = yield select((state: RootState) => state.Tab.tabs.find(x => x.tabKey == state.Tab.activeTabKey));
    const processcode = tabDetail?.tabActualKey as string;

    let findTags = yield call(handleGetScreenHelpRequiredTags, payload);

    const filterObject = {
      isAccountDimension: !!payload.isAccountDimension,
      dimensionValues: payload?.dimensionValues
    };


    const helpConfig = yield call(LoadHelpObject, payload.helpObject, payload.helpWhere, processcode, findTags, filterObject);

    const help = {
      config: helpConfig,
      helpObjectNo: payload.helpObject,
      helpWhere: payload.helpWhere,
      helpTags: findTags,
      tabKey: tabDetail.tabKey,
      returnParams: payload.returnParams,
      helpType: payload.helpType || EHelpType.SideHelp,
      helpOpenFor: payload.returnParams.gridno ? EHelpOpenFor.Grid : EHelpOpenFor.Field,
      isSubGrid: payload.isSubGrid || false,
      isDropDown: payload.isDropDown,
      helpOpenIn: payload.helpOpenIn || EHelpOpenIn.EntryForm,
      multiSelect: payload.multiSelect || false,
      shortHelpOptions: payload.shortHelpOptions || undefined,
      issetting: false
    };

    yield put(setHelpConfig(help));

  }
  catch (error: any) {
    yield put(setGlobalMessage({ error }));
  }
  finally {

    yield put(toggleLoader(false));

  }
}

export function* handlerLoadHelpData(action: PayloadAction<{ objectId: string, filter: Partial<Exclude<IDocumentHelpView["setting"], "frozenColumns" | "columnIndex" | "columnWidth" | "columnHidden">> }, string, { helpLoaded: () => void } | undefined>): any {
  try {
    const payload = action.payload;
    yield put(toggleLoader(true));

    const processcode = yield select((state: RootState) => state.Tab.tabs.find(x => x.tabKey == payload.objectId)?.tabActualKey);
    const helpConfig = yield select((state: RootState) => state.Help[payload.objectId].loadedHelp);

    const appliedView: IDocumentHelpView["setting"] = yield select((state: RootState) => {
      const helpLoaded = state.Help[payload.objectId].loadedHelp;
      const helpOpenIn = helpLoaded?.helpOpenIn
      const helpType = helpLoaded?.helpType
      if (helpOpenIn == EHelpOpenIn.EntryForm && helpType == EHelpType.DocumentHelp) {
        return state.EntryForm.objects[payload.objectId].otherState.appliedDocumentHelpView.setting;
      }
      else if (helpType?.isEqual(EHelpType.SideHelp)) {
        return helpLoaded?.helpView;
      }
      else {
        return {};
      }
    });

    let filterCondition: IDocumentHelpView["setting"]["filterCondition"] = appliedView?.filterCondition || [];
    if (payload.filter.filterCondition?.length) {
      const newConditions = payload.filter.filterCondition;
      filterCondition = filterCondition.filter(x => newConditions.findIndex(y => y.columnId == x.columnId) == -1);
      const newActiveConditions = newConditions.filter(x => x.active1 || x.active2);

      if (newActiveConditions.length)
        filterCondition.push(...newActiveConditions);
    }

    const filter = {
      ...appliedView,
      ...payload.filter,
      filterCondition,
      serverSideProcess: helpConfig.helpConfig.Config.serverpaging
    }

    const response = yield call(requestGetLoadHelpData, helpConfig.helpObjectNo, helpConfig.helpWhere, helpConfig.helpTags as KeyValueObject, processcode, filter);

    if (helpConfig.helpType.isEqual(EHelpType.SideHelp)) {
      yield put(setHelpView({
        tabKey: payload.objectId, view: {
          filterCondition: filter.filterCondition,
          orderBy: filter.orderBy,
          pageIndex: filter.pageIndex,
          pageSize: filter.pageSize,
          wholeSearchParameter: filter.wholeSearchParameter,
          wholeSearchValue: filter.wholeSearchValue
        }
      }));
    }

    yield put(setHelpDetailData({ tabKey: payload.objectId, data: response }));

    if (action.meta?.helpLoaded)
      yield call(action.meta.helpLoaded);

  }
  catch (error: any) {
    yield put(setGlobalMessage({ error }));
  }
  finally {
    yield put(toggleLoader(false));
  }
}

export function* handleGetScreenHelpRequiredTags(payload: IGetHelp): any {

  let helpTags = yield call(LoadHelpObjectTags, payload.helpObject, payload.helpWhere);

  let findTags: any = {};

  if (payload.helpOpenIn == EHelpOpenIn.EntryForm) {
    if (payload.isSubGrid) {

      let subGrids = yield select((state: RootState) => state.EntryForm.objects[state.EntryForm.activeObjectId].subGrid as Array<ISubGrid>);

      for (let i = subGrids.length - 1; i > -1; i--) {
        const element = subGrids[i] as ISubGrid;

        let rowIndex;
        if (i == subGrids.length - 1) { // for last grid
          rowIndex = payload.returnParams.gridRowIndex as number;
        }
        else { // for previous grid
          rowIndex = subGrids[i + 1].parentRowIndex;
        }

        const model = element.modelData[rowIndex];

        if (model)
          helpTags.map((x: any) => {
            if (model[x] != undefined)
              findTags[x] = model[x] || "";
          });

      }

    }

    if (payload.returnParams.gridno) {
      let model: any = {};
      const collectionName = payload.returnParams.gridno.getCollectionNameFromGridno();

      if (payload.isSubGrid)
        model = yield select((state: RootState) => {
          const allSubGrids = state.EntryForm.objects[state.EntryForm.activeObjectId].subGrid as Array<ISubGrid>;

          const parentGridno = allSubGrids[allSubGrids.length - 1].parentGridno.getCollectionNameFromGridno();
          const parentRowIndex = allSubGrids[allSubGrids.length - 1].parentRowIndex;

          return state.EntryForm.objects[state.EntryForm.activeObjectId].model[parentGridno]?.[parentRowIndex];
        });
      else
        model = yield select((state: RootState) => state.EntryForm.objects[state.EntryForm.activeObjectId].model[collectionName as string]?.[payload.returnParams.gridRowIndex as number]);

      if (model)
        helpTags.map((x: any) => {
          if (model && model[x] != undefined)
            findTags[x] = model[x] || "";
        });
    }
  }

  if (payload.helpOpenIn == EHelpOpenIn.EntryForm) {
    const model = yield select((state: RootState) => state.EntryForm.objects[state.EntryForm.activeObjectId].model);

    helpTags.map((x: any) => {
      if (findTags[x] == undefined)
        findTags[x] = model[x] || "";
    });

    if (!findTags["processcode"]) {
      const tabExists = yield select((state: RootState) => state.Tab.tabs.find(x => x.tabKey == state.Tab.activeTabKey));
      if (tabExists) {
        const processcode = tabExists.tabActualKey;
        findTags["processcode"] = processcode;
      }
    }
    if (!findTags["clientno"])
      delete findTags["clientno"];
  }
  else if (payload.helpOpenIn?.isEqual(EHelpOpenIn.ReportExplorer, EHelpOpenIn.DataAnalytics)) {
    let model: KeyValueObject<ICriteriaFieldValue>;

    if (payload.helpOpenIn?.isEqual(EHelpOpenIn.DataAnalytics))
      model = yield select((state: RootState) => state.DataAnalytics.dataAnalyticsDetailObject[state.DataAnalytics.activeDataAnalyticsObjectno].modelValue);
    else
      model = yield select((state: RootState) => state.ReportExplorer.reportDetailObject[state.ReportExplorer.activeReportObjectno].modelValue);

    helpTags.map((x: any) => {
      if (findTags[x] == undefined)
        findTags[x] = model[x]?.fromValue || "";
    });
  }
  else if (payload.helpOpenIn?.isEqual(EHelpOpenIn.Dashboard)) {
    let model: KeyValueObject<ICriteriaFieldValue> = yield select((state: RootState) => state.Dashboard.activeWidget.modelValue);
    helpTags.map((x: any) => {
      if (findTags[x] == undefined)
        findTags[x] = model[x]?.fromValue || "";
    });
  }

  if (!findTags["languageno"])
    delete findTags["languageno"];

  if (payload.helpOpenIn?.isEqual(EHelpOpenIn.DataAnalytics, EHelpOpenIn.ReportExplorer)) {
    if (!findTags["legalentityno"])
      delete findTags["legalentityno"];
  }

  return findTags;

}
